import React, { useState } from "react";
import { Box, useTheme } from "@mui/material";
import Button from "../../components/Button";
import AddIcon from "@mui/icons-material/Add";
import { pushErrorMessage } from '../../utils/notifications';
import UsersDataGrid from "./components/usersDataGrid";
import Popup from '../../components/Popup';
import { FormattedMessage } from "react-intl";
import messages from "./components/messages";
import AddEditUserDialog from "./components/addEditUserDialog";
import { tokens } from "../../assets/theme";
import { useQuery } from "@tanstack/react-query";
import CircularProgress from "@mui/material/CircularProgress";
import { useMsal, useAccount } from "@azure/msal-react";
import AxionControls from "../../components/controls/AxionControls";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { fetchUsers, updateUser, createUser, deleteUser } from "../../fetchers/user";
import { fetchRoles } from "../../fetchers/role";
import { useAuth } from "../../auth/AuthContext";
import { hasAnyRole } from "../../auth/authUi";

const UsersIndex = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { accounts } = useMsal();
    const account = useAccount(accounts[0]);
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [openPopup, setOpenPopup] = useState(false);
    const [user, setUser] = useState({
        id: null,
        username: "",
        name: "",
        email: "",
        roles: []
    });
    const [showBackdrop, setShowBackdrop] = useState(false);

    const usersQuery = useQuery(["users"], () => fetchUsers(),
        {
            keepPreviousData: true
        }
    );

    const rolesQuery = useQuery(["roles"], () => fetchRoles(),
        {
            keepPreviousData: true
        }
    );
    const { roleIds } = useAuth();

    const handleAddUserClick = () => {
        setUser({
            id: null,
            username: "",
            name: "",
            email: "",
            roles: [],
        });
        setOpenPopup(true);
    }

    const handleEditUserClick = (id) => {
        const user = usersQuery.data.find((rowData) => rowData.id === id);
        setUser(user);
        setOpenPopup(true);
    }

    const handleDeleteUserClick = (id) => {
        handleDelete(id);
    }

    const handleChange = (event) => {
        const { id, name, value, type, checked } = event.target
        setUser(prev => {
            return {
                ...prev,
                [id ? id : name]: type === "checkbox" ? checked : value
            }
        })
    }

    const handleRolesChange = (event) => {
        const { value } = event.target;
        setUser(prev => ({
            ...prev,
            roles: value
        }));
    }

    const handleSave = () => {
        if(user.roles.includes(1) && !hasAnyRole(roleIds, [1])) {
            pushErrorMessage("Only users in Admin role can assign users to Admin role.");
            return;
        }
        setShowBackdrop(true);
        if (user.id) {
            updateUser(user).then(() => {
                setOpenPopup(false);
                usersQuery.refetch();
                setShowBackdrop(false);
            }).catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot update the user.`);
            });
        } else {
            createUser(user).then(() => {
                setOpenPopup(false);
                usersQuery.refetch();
                setShowBackdrop(false);
            }).catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot create the user.`);
            });
        }
    }

    const handleDelete = (id) => {
        setShowBackdrop(true);
        deleteUser(id).then(() => {
            usersQuery.refetch();
            setShowBackdrop(false);
        })
            .catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot delete the user.`);
            });
    }

    if (usersQuery.isLoading || rolesQuery.isLoading)
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress color="secondary" /></div>;

    return (
        <Box maxWidth={1800} >
            <Button
                text={<FormattedMessage {...messages.addUserLabel} />}
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddUserClick}
                sx={{
                    "&:hover": {
                        backgroundColor: colors.dashbordGreen[700],
                    },
                    backgroundColor: colors.dashbordGreen[500],
                    color: "#000000",
                    position: "absolute",
                    right: "10px",
                }} />
            <UsersDataGrid
                users={usersQuery}
                roles={rolesQuery.data}
                handleDeleteUserClick={handleDeleteUserClick}
                handleEditUserClick={handleEditUserClick}
                isPopupOpen={openPopup} />
            <Popup
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                maxWidht="sm"
                title={user.id != null ? <FormattedMessage {...messages.editUserLabel} /> : <FormattedMessage {...messages.addUserLabel} />}>
                <AddEditUserDialog
                    onChange={handleChange}
                    onRolesChange={handleRolesChange}
                    handleSave={handleSave}
                    user={user}
                    roles={rolesQuery.data}
                    setOpenPopup={setOpenPopup} />
            </Popup>
            <AxionControls.AxionBackdrop open={showBackdrop} />
        </Box>
    );
};

export default UsersIndex;
